import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import CubeLoader, { BUTTON_SIZE } from 'common/components/CubeLoader';
import { classNames } from 'utils/classNames';
import './styles.styl';

const BASE_CLASS_NAME = 'BurstButton';
const ANIMATION_TIMING = 1600;

const getClasses = (className, currentlyLoading, loadEnded, disabled, hasSaveError) => {
    const classes = {
        [BASE_CLASS_NAME]: BASE_CLASS_NAME,
        loading: currentlyLoading,
        done: loadEnded,
        error: hasSaveError,
        disabled: !currentlyLoading && !loadEnded && disabled,
        [className]: className,
    };
    return classNames(classes);
};

const BurstButton = ({
    className,
    disabled,
    isLoading,
    label,
    labelDone,
    offlineLabel,
    onClick,
    hasSaveError,
    ...restProps
}) => {
    const [currentlyLoading, setCurrentlyLoading] = useState(false);
    const [loadEnded, setLoadEnded] = useState(false);

    // Keep track of component being mounted.
    const componentIsMounted = useRef(true);
    useEffect(
        () => () => {
            componentIsMounted.current = false;
        },
        [],
    );

    useEffect(() => {
        // start loading
        if (isLoading && !currentlyLoading) {
            setCurrentlyLoading(true);
        }

        // stop Loading
        if (!isLoading && currentlyLoading) {
            setCurrentlyLoading(false);
            if (__TESTING__) {
                setLoadEnded(false);
            } else {
                setLoadEnded(true);
                setTimeout(() => {
                    if (componentIsMounted.current) {
                        setLoadEnded(false);
                    }
                }, ANIMATION_TIMING);
            }
        }

        // stop button loader animation if save failed
        if (hasSaveError) {
            setCurrentlyLoading(false);
            setLoadEnded(false);
        }
    });

    const isDisabled = disabled || currentlyLoading || loadEnded;

    return (
        <button
            className={getClasses(className, currentlyLoading, loadEnded, disabled, hasSaveError)}
            onClick={isDisabled ? null : onClick}
            aria-disabled={isDisabled}
            {...restProps}
        >
            <div className={`${BASE_CLASS_NAME}__animation-circle circle1`} />
            <div className={`${BASE_CLASS_NAME}__animation-circle circle2`} />
            <div className={`${BASE_CLASS_NAME}__label default-label`}>{!hasSaveError ? label : offlineLabel}</div>
            <div className={`${BASE_CLASS_NAME}__label loading-label`}>
                <CubeLoader size={BUTTON_SIZE} useAlternate delay={0} loading={currentlyLoading || loadEnded} />
            </div>
            <div className={`${BASE_CLASS_NAME}__label done-label`}>{labelDone}</div>
        </button>
    );
};

BurstButton.propTypes = {
    // [string] Additional classes to be added to the .BurstButton div
    className: PropTypes.string,
    // [boolean] Determines if the button is disabled
    disabled: PropTypes.bool,
    // [boolean] Tells the button whether or not the app is currently loading
    isLoading: PropTypes.bool,
    // [node] The label/element to be displayed on the button by default
    label: PropTypes.node.isRequired,
    // [node] The label/element to be displayed on the button when finished loading
    labelDone: PropTypes.node.isRequired,
    // [function] The callback function that will be invoked when the button is clicked
    onClick: PropTypes.func.isRequired,
    // [boolean] Determines if the error occured during save and button color has to be changeg
    hasSaveError: PropTypes.bool.isRequired,
    // [node] The label/element to be displayed on the button on save error
    offlineLabel: PropTypes.node.isRequired,
};

BurstButton.defaultProps = {
    className: '',
    disabled: false,
    isLoading: false,
};

export default BurstButton;
